/**
 * The AI duty officer. Advisory only, enforced server-side: no reply from these
 * endpoints can reach the vehicle registry, so the model can suggest and never
 * actuate. Latency is seconds, not frames — callers show a busy state.
 */

import { api, type ApiResult } from '@/api/client';
import type { Waypoint } from '@/types/domain';

/** The model call can sit in a cloud queue; don't abort under it. */
const AI_TIMEOUT_MS = 95_000;

export interface AiStatus {
  configured: boolean;
  model: string;
}

export interface AiAlert {
  id: string;
  severity: 'critical' | 'caution' | string;
  reason: string;
  action: string;
}

export function getAiStatus(): Promise<ApiResult<AiStatus>> {
  return api.get<AiStatus>('/ai/status', { logged: false });
}

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export function askAi(
  question: string,
  history: ChatTurn[] = [],
): Promise<ApiResult<{ answer: string; model: string; latencyMs: number }>> {
  // The server caps history at 12 turns; send the tail so a long session still
  // fits and the officer keeps the most recent thread of the conversation.
  return api.post(
    '/ai/ask',
    { question, history: history.slice(-12) },
    { label: 'AI ASK', timeoutMs: AI_TIMEOUT_MS },
  );
}

export function triageFleet(): Promise<
  ApiResult<{ alerts: AiAlert[]; raw?: string; model: string; latencyMs: number }>
> {
  return api.post('/ai/triage', undefined, { label: 'AI TRIAGE', timeoutMs: AI_TIMEOUT_MS });
}

export function critiquePlan(
  droneId: string,
  waypoints: Waypoint[],
  notes = '',
): Promise<ApiResult<{ critique: string; model: string; latencyMs: number }>> {
  return api.post(
    '/ai/critique-plan',
    { droneId, waypoints, notes },
    { label: 'AI PLAN CRITIQUE', timeoutMs: AI_TIMEOUT_MS },
  );
}

export function summarizeFlight(
  sessionId: string,
): Promise<ApiResult<{ summary: string; model: string; latencyMs: number }>> {
  return api.post(
    '/ai/summarize-flight',
    { sessionId },
    { label: 'AI FLIGHT SUMMARY', timeoutMs: AI_TIMEOUT_MS },
  );
}


export interface AiBriefing {
  briefing: string;
  model: string;
  latencyMs: number;
}

/** A shift-change situation brief of the live fleet. */
export function getBriefing(): Promise<ApiResult<AiBriefing>> {
  return api.post<AiBriefing>('/ai/briefing', {}, { logged: false });
}
